import { FEATURES, ICounterData } from "@home-health-notify/domain";
import ChatBubbleOutlinedIcon from "@mui/icons-material/ChatBubbleOutlined";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import { Badge, Tooltip } from "@mui/material";
import { OrganizationContext, useTranslation } from "hhn-commons";
import { useContext } from "react";
import { RailContainer, RailIconButton } from "./LeftPanel.style";

export interface LeftPanelCompactProps {
  counters: ICounterData;
  // Index of the rail icon to render as active.
  activeTab: number;
  // Click handler — receives the clicked tab index.
  onTabClick: (tabIndex: number) => void;
  // When true the rail collapses to width 0 (hidden while chatlist is open).
  collapsed?: boolean;
}

const CHATS_TAB = 0;
const DIRECTORY_TAB = 1;
const ALERTS_TAB = 2;

export const LeftPanelCompact = ({
  counters,
  activeTab,
  onTabClick,
  collapsed = false,
}: LeftPanelCompactProps) => {
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "chat" });

  const hasDirectory =
    selectedOrgUser?.hasAccess?.(FEATURES.VIEW_USER_DIRECTORY) ?? false;

  const tabs = [
    {
      index: CHATS_TAB,
      label: tNs("messages"),
      icon: <ChatBubbleOutlinedIcon />,
      count: counters.messageCounter,
      access: true,
      dataTestId: "rail-messages",
    },
    {
      index: DIRECTORY_TAB,
      label: tNs("directory"),
      icon: <PeopleOutlinedIcon />,
      count: 0,
      access: hasDirectory,
      dataTestId: "rail-directory",
    },
    {
      index: ALERTS_TAB,
      label: tNs("alert"),
      icon: <NotificationsNoneIcon />,
      count: counters.alertCounter,
      access: true,
      dataTestId: "rail-alerts",
    },
  ];

  return (
    <RailContainer $collapsed={collapsed} aria-hidden={collapsed}>
      {tabs
        .filter(t => t.access)
        .map(tab => (
          <Tooltip key={tab.index} title={tab.label} placement="right">
            <RailIconButton
              data-testid={tab.dataTestId}
              aria-label={tab.label}
              isActive={activeTab === tab.index}
              onClick={() => onTabClick(tab.index)}
            >
              <Badge
                color="primary"
                badgeContent={tab.count}
                max={99}
                invisible={tab.count <= 0}
              >
                {tab.icon}
              </Badge>
            </RailIconButton>
          </Tooltip>
        ))}
    </RailContainer>
  );
};
